import { Typography, TypographyProps } from "@cappelendamm/design-system/react";
import classNames from "classnames";
import Image from "next/image";
import { type JSX } from "react";
import Contributor from "@/utils/contributor.svg";
import { ContainerLink } from "@/navigation/components/ContainerLink";

export type AutocompleteTemplateItemProps = {
  format?: string | null;
  language?: string | null;
  imageSrc?: string | null;
  altText: string;
  highlightName: React.ReactNode;
  highlightDetails?: React.ReactNode;
  price?: string | null;
  itemType?: "contributor" | "book" | "literary-work" | "series" | "article";
  href?: string;
  children?: React.ReactNode;
  className?: string;
};

export type AutocompleteTemplateHeaderProps = TypographyProps &
  JSX.IntrinsicElements["p"];

export function AutocompleteTemplateHeader({
  children,
  className,
  ...rest
}: AutocompleteTemplateHeaderProps) {
  return (
    <Typography
      as="p"
      className={classNames(
        "mb-2 border-b border-stroke-default px-2 pb-2 font-semibold",
        className,
      )}
      {...rest}
    >
      {children}
    </Typography>
  );
}

export function AutocompleteTemplateItem({
  imageSrc,
  format,
  language,
  altText,
  highlightName,
  highlightDetails,
  children,
  href,
  className,
  itemType,
}: AutocompleteTemplateItemProps) {
  return (
    <ContainerLink link={{ href }} tabIndex={0}>
      <div
        className={classNames(
          "relative grid cursor-pointer grid-flow-col grid-cols-[48px_1fr_auto] items-center gap-x-6 px-2 py-2",
          className,
        )}
      >
        {imageSrc && (
          <Image
            src={imageSrc}
            alt={altText}
            height={24}
            width={48}
            className="min-w-12 justify-self-center object-contain"
          />
        )}
        {!imageSrc && itemType === "contributor" && (
          <Contributor className="aspect-square h-[48px] w-[48px] justify-self-center text-gray-200" />
        )}
        {!imageSrc && itemType !== "contributor" && (
          <div className="h-[48px] w-[34px] justify-self-center bg-gray-200" />
        )}
        <div className="flex h-fit flex-col gap-x-0 truncate sm:flex-row">
          <span className="h-5 truncate font-bold">{highlightName}</span>
          {highlightDetails && (
            <span className="truncate sm:before:mr-1 sm:before:content-[',']">
              {highlightDetails}
            </span>
          )}
          {format && (
            <span className="truncate sm:before:mr-1 sm:before:content-[',']">
              {format}
            </span>
          )}
          {language && (
            <span className="truncate sm:before:mr-1">({language})</span>
          )}
        </div>
        {children}
      </div>
    </ContainerLink>
  );
}
